/**
 * 앱 소셜 로그인 크루 핸드오프 — 클라이언트(앱 WebView) 쪽 nonce 보관.
 *
 * native-oauth.ts 가 로그인 창의 첫 URL 을 /api/crew/auth-handoff 로 감쌀 때 nonce 를
 * 만들어 여기 두고, oauth-deep-link.ts 가 setSession 직후 꺼내 claimCrewHandoff 에 넘긴다.
 * Android 는 콜백이 appUrlOpen 으로 따로 들어오므로(같은 WebView, 다른 호출 스택) 메모리가
 * 아니라 localStorage 에 둔다. 한 번 꺼내면 지운다(재사용 금지).
 */

import { CREW_HANDOFF_TTL_SECONDS } from "./app-handoff";

const STORAGE_KEY = "la_crew_handoff_nonce";

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** 24바이트 난수 → base64url 32자(DB CHECK 20..128 안). */
export function createCrewHandoffNonce(): string | null {
  try {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    const nonce = base64Url(bytes);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ n: nonce, t: Date.now() }));
    } catch {
      // 저장 못 하면 회수도 못 한다 — 감싸지 않는 쪽이 낫다(호출자가 원 URL 을 쓴다).
      return null;
    }
    return nonce;
  } catch {
    return null;
  }
}

/** 보관된 nonce 를 꺼내고 지운다. 수명(15분)을 넘긴 것은 버린다. */
export function takeCrewHandoffNonce(): string | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    localStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as { n?: unknown; t?: unknown };
    if (typeof parsed.n !== "string" || typeof parsed.t !== "number") return null;
    if (Date.now() - parsed.t > CREW_HANDOFF_TTL_SECONDS * 1000) return null;
    return parsed.n;
  } catch {
    return null;
  }
}

/**
 * 로그인 창의 첫 URL 을 핸드오프 라우트로 감싼다. origin 은 앱 WebView 의 출처(=server.url)
 * — 크루 링크가 la_ref 를 심은 바로 그 호스트다. nonce 를 만들 수 없으면 원 URL 그대로.
 */
export function wrapWithCrewHandoff(authUrl: string): string {
  if (typeof window === "undefined") return authUrl;
  const nonce = createCrewHandoffNonce();
  if (!nonce) return authUrl;
  const params = new URLSearchParams({ nonce, continue: authUrl });
  return `${window.location.origin}/api/crew/auth-handoff?${params.toString()}`;
}
